import { useEffect, useState } from "react";
import {
  AlertCircle,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Shield,
  UserPlus,
  Users,
} from "lucide-react";
import {
  createLicenseCredential,
  listUsers,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  ComplianceRole,
  LicenseCredentialItem,
  UserItem,
} from "../../types/compliance";

const ROLES = [
  "COMPLIANCE_ADMIN",
  "FINANCE_REVIEWER",
  "SHIPMENT_OPERATOR",
  "AUDITOR",
] as const;

const ROLE_LABELS: Record<string, string> = {
  COMPLIANCE_ADMIN: "Admin",
  FINANCE_REVIEWER: "Finance Reviewer",
  SHIPMENT_OPERATOR: "Shipment Operator",
  AUDITOR: "Auditor",
};

const ROLE_COLORS: Record<string, string> = {
  COMPLIANCE_ADMIN: "bg-purple-100 text-purple-800",
  FINANCE_REVIEWER: "bg-blue-100 text-blue-800",
  SHIPMENT_OPERATOR: "bg-teal-100 text-teal-800",
  AUDITOR: "bg-gray-100 text-gray-700",
};

export default function UserManagement() {
  const { credentials } = useComplianceAuth();
  const isAdmin = credentials?.role === "COMPLIANCE_ADMIN";

  setComplianceContext(
    credentials?.tenantId ?? "default",
    credentials?.role ?? "COMPLIANCE_ADMIN",
  );

  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<(typeof ROLES)[number]>("AUDITOR");
  const [creating, setCreating] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [newLicenseKey, setNewLicenseKey] = useState<string | null>(null);
  const [newCredential, setNewCredential] =
    useState<LicenseCredentialItem | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await listUsers();
      setUsers(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin]);

  function validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function handleCreate() {
    setEmailError(null);
    if (!validateEmail(newEmail)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setCreating(true);
    setError(null);
    setNewLicenseKey(null);
    setNewCredential(null);
    try {
      const tenantId = credentials?.tenantId ?? "default";
      const response = await createLicenseCredential({
        tenant_id: tenantId,
        client_name: tenantId,
        contact_email: newEmail.trim(),
        role: newRole as ComplianceRole,
        notes: "Created from tenant user management",
      });
      const user: UserItem = {
        user_id: response.credential.user_id ?? response.credential.license_id,
        tenant_id: response.credential.tenant_id,
        email: response.credential.contact_email,
        role: response.credential.role,
        created_at: response.credential.created_at,
      };
      setUsers((prev) => [user, ...prev]);
      setNewLicenseKey(response.license_key);
      setNewCredential(response.credential);
      setNewEmail("");
      setNewRole("AUDITOR");
      setShowCreate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user access key");
    } finally {
      setCreating(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full">
        <Shield size={48} className="text-gray-300 mb-4" />
        <h2 className="text-lg font-semibold text-gray-700 mb-1">
          Admin Access Required
        </h2>
        <p className="text-sm text-gray-500">
          User management is restricted to Compliance Admins.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users size={24} className="text-violet-600" />
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition"
          >
            <UserPlus size={14} />
            Add User
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Portal users sign in with tenant ID, role, and a generated access key.
        Passwords are not used for Finance Reviewer, Auditor, or Operator access.
      </div>

      {newLicenseKey && newCredential && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <KeyRound size={15} />
                Access key generated for {newCredential.contact_email}
              </h2>
              <p className="mt-1 text-xs text-emerald-700">
                Share this securely. It is shown only once and replaces a password.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void navigator.clipboard?.writeText(newLicenseKey)}
              className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <Copy size={13} />
              Copy Key
            </button>
          </div>
          <code className="mt-3 block break-all rounded bg-white px-3 py-2 text-xs font-semibold text-emerald-900">
            {newLicenseKey}
          </code>
        </div>
      )}

      {/* Create panel */}
      {showCreate && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6">
          <h2 className="text-sm font-semibold text-violet-900 mb-3 flex items-center gap-1.5">
            <Plus size={14} />
            Add New User Access
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setEmailError(null);
                }}
                placeholder="user@company.com"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 ${
                  emailError
                    ? "border-red-400 bg-red-50"
                    : "border-gray-300"
                }`}
              />
              {emailError && (
                <p className="text-xs text-red-600 mt-1">{emailError}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={newRole}
                aria-label="User role"
                onChange={(e) =>
                  setNewRole(e.target.value as (typeof ROLES)[number])
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || !newEmail}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 rounded-lg transition"
            >
              {creating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <UserPlus size={14} />
              )}
              Add User
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewEmail("");
                setEmailError(null);
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg mb-4">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* User table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-800">
            Tenant Users
          </span>
          <span className="text-xs text-gray-500">
            {users.length} user{users.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading && users.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">
            No users found. Add one above.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Email
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Role
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  User ID
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Added
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {u.email}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        ROLE_COLORS[u.role] ?? "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {ROLE_LABELS[u.role] ?? u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {u.user_id}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
